/**
 * +field: the player's one character (save v5, docs/design/zones.md): a new HeroCharacter and the
 * constants other modules share (kept apart from field.ts so profile.ts can repair saves without an
 * import cycle).
 */
import type { ClassId } from '@keystone/sim';
import { START_POINT, getContentBundle } from '@keystone/content';
import { emptyBountyState } from './bounties';
import type { HeroCharacter } from './types';

/** Kill reports refill at this many kills per second of server time... */
export const FIELD_KILLS_PER_SEC = 1.5;
/** ...up to this many banked (a long fight's burst); kills past the budget are dropped silently. */
export const FIELD_KILL_BURST = 40;
/** Where a new character starts. */
export const START_ZONE = 'mossvale';
export const START_MAP = START_POINT.mapId;
/** Look presets per class (0 = the class's default model). */
export const HERO_LOOKS = 8;

export function newHeroCharacter(classId: ClassId, name: string, look: number): HeroCharacter {
  return {
    name,
    classId,
    look,
    zoneId: START_ZONE,
    mapId: START_MAP,
    // In front of Elder Rowan, the main quest's first step.
    pos: { x: START_POINT.pos.x, y: START_POINT.pos.y },
    facing: START_POINT.facing,
    bounties: emptyBountyState(),
    kills: 0,
    report: { lastMs: null, budget: FIELD_KILL_BURST },
    dungeonClears: {},
    dungeonEnteredMs: null,
    dungeonEnteredId: null,
    // +zone: the home waystone (nearest the start) is attuned from the first step.
    waystones: homeWaystone(),
  };
}

/** +zone: `mapId:waystoneId` of the waystone nearest the start point (none on maps without waystones). */
function homeWaystone(): string[] {
  const d = getContentBundle().dungeons.find((x) => x.id === START_MAP);
  const stones = d?.field?.waystones ?? [];
  let best: { id: string; d: number } | null = null;
  for (const w of stones) {
    const dd = Math.hypot(w.pos.x - START_POINT.pos.x, w.pos.y - START_POINT.pos.y);
    if (!best || dd < best.d) best = { id: w.id, d: dd };
  }
  return best ? [`${START_MAP}:${best.id}`] : [];
}
